import { useEffect, useState, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Form, FormikProvider, useFormik } from "formik";
import * as Yup from "yup";
import { Box, TextField } from "@mui/material";
import { LoadingButton } from "@mui/lab";
import { motion } from "framer-motion";
import { parse as parseDomain } from "tldts";
import authentication from "../services/AmplifyServices";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import "./style.css";
import { GoogleReCaptchaProvider, useGoogleReCaptcha } from "react-google-recaptcha-v3";
import AmplifyEnv from "../constants/AmplifyEnv";

const easing = [0.6, -0.05, 0.01, 0.99];
const animate = {
  opacity: 1,
  y: 0,
  transition: { duration: 0.6, ease: easing, delay: 0.16 },
};

const SUBMIT_COOLDOWN_MS = 5000;
// TEST: set to true to reuse the same token and trigger timeout-or-duplicate
const TEST_REUSE_TOKEN = false;

const COMMON_DOMAINS = ["gmail.com", "outlook.com", "hotmail.com", "yahoo.com", "icloud.com", "1receipt.io"];

const textMetrics = {
  fontFamily: "inherit",
  fontSize: "1rem",
  lineHeight: "1.4375em",
  letterSpacing: "0.15px",
  fontWeight: 400,
  WebkitFontSmoothing: "antialiased",
  MozOsxFontSmoothing: "grayscale",
};

const hasValidVerification = (email) => {
  if (!email) return false;
  const trimmed = String(email).trim();
  const at = trimmed.lastIndexOf("@");
  if (at === -1 || at === trimmed.length - 1) return false;
  const info = parseDomain(trimmed.slice(at + 1).toLowerCase());
  return Boolean(info.domain && info.publicSuffix && info.isIcann);
};

const getDomainRemainder = (input) => {
  if (!input) return "";
  const at = input.lastIndexOf("@");
  if (at <= 0) return "";
  const local = input.slice(0, at);
  const domainPart = input.slice(at + 1).toLowerCase();
  if (!local || /\s/.test(local) || !domainPart) return "";
  const match = COMMON_DOMAINS.find((d) => d.startsWith(domainPart) && d !== domainPart);
  return match ? match.slice(domainPart.length) : "";
};

const SigninSchema = Yup.object().shape({
  email: Yup.string()
    .email("Provide a valid email address")
    .required("Email is required")
    .test("tld-domain", "Please enter a valid email", (value) => hasValidVerification(value)),
});

const readPrefillEmail = () => {
  try {
    return sessionStorage.getItem("prefillEmail") || localStorage.getItem("prefillEmail") || "";
  } catch {
    return "";
  }
};

const useIsDark = () => {
  const [isDark, setIsDark] = useState(
    document.documentElement.getAttribute("data-theme") === "dark"
  );
  useEffect(() => {
    const el = document.documentElement;
    const obs = new MutationObserver(() => setIsDark(el.getAttribute("data-theme") === "dark"));
    obs.observe(el, { attributes: true, attributeFilter: ["data-theme"] });
    return () => obs.disconnect();
  }, []);
  return isDark;
};

const SigninForm = () => {
  const navigate = useNavigate();
  const { search } = useLocation();
  const { executeRecaptcha } = useGoogleReCaptcha();
  const isDark = useIsDark();
  const lastSubmitRef = useRef(0);
  const [initialEmail] = useState(readPrefillEmail);
  const [domainRemainder, setDomainRemainder] = useState(() => getDomainRemainder(initialEmail));

  useEffect(() => {
    const redirectURL = new URLSearchParams(search).get("redirect");
    if (redirectURL) localStorage.setItem("redirectURL", redirectURL);
  }, [search]);

  useEffect(() => {
    if (!initialEmail) return;
    try {
      sessionStorage.removeItem("prefillEmail");
      localStorage.removeItem("prefillEmail");
    } catch {}
  }, [initialEmail]);

  const formik = useFormik({
    initialValues: { email: initialEmail },
    validationSchema: SigninSchema,
    onSubmit: async ({ email }) => {
      const now = Date.now();
      if (now - lastSubmitRef.current < SUBMIT_COOLDOWN_MS) {
        toast.warn("Please wait a few seconds before trying again.", { toastId: "too-fast" });
        return;
      }
      lastSubmitRef.current = now;

      if (!executeRecaptcha) {
        toast.error("Security verification is not ready. Please try again.");
        return;
      }

      let recaptchaToken;
      try {
        recaptchaToken = await executeRecaptcha("signin");

        if (TEST_REUSE_TOKEN) {
          recaptchaToken = window.__lastToken || recaptchaToken;
          window.__lastToken = recaptchaToken;
          console.log("TEST: reusing reCAPTCHA token");
        }

        if (!recaptchaToken) {
          toast.error("Security verification failed. Please try again.");
          return;
        }
      } catch (error) {
        console.error("reCAPTCHA v3 error:", error);
        toast.error("Unable to complete security verification. Please try again later.");
        return;
      }

      try {
        await authentication.signOut();
        await authentication.signIn(email, recaptchaToken);
        navigate("/otp", { state: { email } });
      } catch (err) {
        console.error("Sign in error:", err);
        const message = err?.message || "";

        if (message.includes("RECAPTCHA_EXPIRED")) {
          toast.warn("Verification expired. Please try again.", { toastId: "recaptcha-expired" });
        } else if (message.includes("reCAPTCHA verification failed")) {
          toast.error("Something didn't look right. Please try again in a moment.", { toastId: "recaptcha-failed" });
        } else if (message === "User does not exist.") {
          toast.info("Email not found. Please sign up.", { toastId: "no-user" });
          const redirect = new URLSearchParams(search).get("redirect");
          if (redirect) setTimeout(() => { window.location = redirect; }, 1300);
        } else if (message.includes("RECAPTCHA_TIMEOUT") || message.includes("aborted due to timeout")) {
          toast.error("Verification timed out. Please try again.", { toastId: "timeout" });
        } else {
          toast.error("An error occurred during sign in. Please try again.");
        }
      }
    },
  });

  const { errors, touched, isSubmitting, handleSubmit, values, setFieldValue, submitForm } = formik;

  const acceptSuggestion = (e, shouldSubmit = false) => {
    if (!domainRemainder) return;
    e?.preventDefault?.();
    setFieldValue("email", values.email + domainRemainder, false);
    setDomainRemainder("");
    if (shouldSubmit) setTimeout(() => submitForm(), 0);
  };

  const handleEmailChange = (e) => {
    const v = e.target.value;
    setFieldValue("email", v, true);
    setDomainRemainder(getDomainRemainder(v));
  };

  const handleEmailKeyDown = (e) => {
    if (!domainRemainder) return;
    if (["Enter", "Tab", "ArrowRight"].includes(e.key)) {
      acceptSuggestion(e, e.key === "Enter");
    }
  };

  return (
    <FormikProvider value={formik}>
      <Form
        autoComplete="off"
        noValidate
        onSubmit={handleSubmit}
        onSubmitCapture={(e) => {
          if (domainRemainder) acceptSuggestion(e, true);
        }}
      >
        <Box component={motion.div} initial={{ opacity: 0, y: 40 }} animate={animate} sx={{ width: "100%" }}>
          <Box sx={{ position: "relative", mt: "20px" }}>
            <TextField
              fullWidth
              autoComplete="username"
              autoFocus
              type="email"
              label="Email Address"
              value={values.email}
              onChange={handleEmailChange}
              onKeyDown={handleEmailKeyDown}
              error={Boolean(touched.email && errors.email)}
              helperText={touched.email && errors.email}
              inputProps={{ spellCheck: false, autoCapitalize: "none", autoCorrect: "off" }}
              InputProps={{
                style: {
                  borderRadius: "25px",
                  backgroundColor: isDark ? "#696969" : "#F4F4F4",
                  ...(isDark ? { color: "#FFF" } : {}),
                  ...textMetrics,
                },
              }}
              sx={{
                "& .MuiOutlinedInput-notchedOutline": { border: "none" },
                "& .MuiOutlinedInput-input": { padding: "16.5px 14px", ...textMetrics },
                ...(isDark ? { "& label.MuiInputLabel-root": { color: "#C4C4C4" } } : {}),
              }}
            />

            {!!domainRemainder && (
              <Box
                onMouseDown={(e) => acceptSuggestion(e)}
                sx={{
                  position: "absolute",
                  inset: 0,
                  zIndex: 2,
                  display: "flex",
                  alignItems: "center",
                  padding: "16.5px 14px",
                  pointerEvents: "auto",
                  userSelect: "none",
                  cursor: "pointer",
                  color: isDark ? "#AAAAAA" : "#9aa0a6",
                  whiteSpace: "pre",
                  ...textMetrics,
                }}
              >
                <span style={{ visibility: "hidden" }}>{values.email}</span>
                <span style={{ display: "inline-block", transform: "translateY(0.5px)" }}>
                  {domainRemainder}
                </span>
              </Box>
            )}
          </Box>

          <LoadingButton
            fullWidth
            size="large"
            type="submit"
            variant="contained"
            loading={isSubmitting}
            sx={{
              marginTop: "20px",
              marginBottom: "3px",
              backgroundColor: isDark ? "#095E39" : "#00C56E",
              boxShadow: "none",
              borderRadius: "25px",
              height: "56px",
              "&:hover": {
                backgroundColor: isDark ? "rgba(0, 197, 110, 0.1)" : "#00B864",
                boxShadow: "none",
              },
            }}
          >
            Continue
          </LoadingButton>
        </Box>
      </Form>
    </FormikProvider>
  );
};

const SigninFormWithRecaptcha = (props) => (
  <GoogleReCaptchaProvider
    reCaptchaKey={AmplifyEnv.G_RECAPTCHA_V3_SITE_KEY}
    scriptProps={{ async: true, defer: true, appendTo: "head" }}
  >
    <SigninForm {...props} />
  </GoogleReCaptchaProvider>
);

export default SigninFormWithRecaptcha;